import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Building2, Briefcase, Landmark, Compass, Plus, Search, X, Pencil, Trash2,
  Download, Send, FileSignature, Upload, Loader2, ChevronRight, Image as ImageIcon,
  CheckCircle2, FileText, Mail, Phone, MapPin, MessageSquare, AlertCircle,
} from 'lucide-react';
import ProspectDossierPanel from '../components/ProspectDossierPanel';
import ProspectionsDashboard from '../components/ProspectionsDashboard';
import ProspectionGuideModal, { FloatingScriptButton } from '../components/ProspectionGuideModal';

const CATEGORY_META = {
  agence: { label: 'Agences immobilières', icon: Building2, color: 'from-blue-500 to-blue-700', accent: 'bg-blue-50 text-blue-700 border-blue-200' },
  syndic: { label: 'Syndics', icon: Briefcase, color: 'from-violet-500 to-violet-700', accent: 'bg-violet-50 text-violet-700 border-violet-200' },
  architecte: { label: 'Architectes', icon: Compass, color: 'from-emerald-500 to-emerald-700', accent: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  mairie: { label: 'Mairies', icon: Landmark, color: 'from-amber-500 to-amber-700', accent: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const STATUTS = [
  'Nouveau', 'Plaquette envoyée', 'Relance 1', 'Relance 2',
  'Intéressé', 'Convention envoyée', 'Convention signée', 'Partenaire actif', 'Refusé',
];
const STATUT_STYLE = {
  'Nouveau': 'bg-slate-100 text-slate-700 border-slate-200',
  'Plaquette envoyée': 'bg-blue-100 text-blue-800 border-blue-200',
  'Relance 1': 'bg-amber-100 text-amber-800 border-amber-200',
  'Relance 2': 'bg-orange-100 text-orange-800 border-orange-200',
  'Intéressé': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Convention envoyée': 'bg-violet-100 text-violet-800 border-violet-200',
  'Convention signée': 'bg-teal-100 text-teal-800 border-teal-200',
  'Partenaire actif': 'bg-green-200 text-green-900 border-green-300',
  'Refusé': 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function Prospections() {
  const nav = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('home'); // 'home' | 'category' | 'detail'
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeProspectId, setActiveProspectId] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideDefaultTab, setGuideDefaultTab] = useState('script');

  useEffect(() => { document.title = 'Prospections — Reno Resolve'; loadStats(); }, []);

  const loadStats = async () => {
    try {
      const r = await api.get('/prospects/stats');
      setStats(r.data || {});
    } catch (_) { /* noop */ }
  };

  const loadCategory = async (cat) => {
    setLoading(true);
    try {
      const r = await api.get(`/prospects`, { params: { category: cat } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Impossible de charger les prospects");
    }
    setLoading(false);
  };

  const goCategory = (cat) => {
    setActiveCategory(cat);
    setView('category');
    loadCategory(cat);
  };

  const [bucket, setBucket] = useState('chaud'); // 'chaud' | 'mort' | 'actif'
  const [showContactModal, setShowContactModal] = useState(null); // prospect object or null
  const [sortMode, setSortMode] = useState('ordre'); // 'ordre' | 'fiabilite' | 'population' | 'nom'
  const [fiabiliteFilter, setFiabiliteFilter] = useState('all'); // 'all' | 'haute' | 'moyenne' | 'à vérifier'

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase();
    let list = items;
    if (q) {
      list = list.filter((p) =>
        [p.nom_societe, p.ville, p.email, p.siret].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    // Filtre fiabilité (uniquement mairies)
    if (activeCategory === 'mairie' && fiabiliteFilter !== 'all') {
      list = list.filter(p => (p.fiabilite || '').toLowerCase() === fiabiliteFilter.toLowerCase());
    }
    // Tri (uniquement mairies)
    if (activeCategory === 'mairie') {
      const rankFiab = { 'haute': 0, 'moyenne': 1, 'à vérifier': 2 };
      const sorters = {
        ordre: (a, b) => (a.ordre_import ?? 9999) - (b.ordre_import ?? 9999),
        fiabilite: (a, b) => (rankFiab[(a.fiabilite || '').toLowerCase()] ?? 3) - (rankFiab[(b.fiabilite || '').toLowerCase()] ?? 3)
                              || (b.population || 0) - (a.population || 0),
        population: (a, b) => (b.population || 0) - (a.population || 0),
        nom: (a, b) => String(a.nom_societe || '').localeCompare(String(b.nom_societe || ''), 'fr'),
      };
      list = [...list].sort(sorters[sortMode] || sorters.ordre);
    }
    return list;
  }, [items, search, activeCategory, sortMode, fiabiliteFilter]);

  const bucketed = useMemo(() => {
    const byBucket = { chaud: [], mort: [], actif: [] };
    filtered.forEach((p) => {
      const b = p.bucket || 'chaud';
      if (byBucket[b]) byBucket[b].push(p);
    });
    return byBucket;
  }, [filtered]);

  // ---------- HOME (4 tuiles) ----------
  if (view === 'home') {
    const openProspect = async (pid, cat) => {
      if (cat) {
        setActiveCategory(cat);
        await loadCategory(cat);
      }
      setActiveProspectId(pid);
      setView('detail');
    };
    return (
      <div className="space-y-6" data-testid="prospections-home">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Prospections</h1>
            <p className="text-slate-500 text-sm mt-1">Pilotage commercial par catégorie : suivi, plaquettes personnalisées, conventions signées.</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setGuideDefaultTab('fiche'); setGuideOpen(true); }}
              data-testid="open-guide-btn"
              className="border-[#1C49A6] text-[#1C49A6] hover:bg-[#1C49A6] hover:text-white"
            >
              <FileText className="w-4 h-4 mr-2" /> Guide commercial
            </Button>
            <Button
              onClick={() => { setGuideDefaultTab('script'); setGuideOpen(true); }}
              data-testid="open-script-btn"
              className="bg-[#1C49A6] hover:bg-[#163C89]"
            >
              <Phone className="w-4 h-4 mr-2" /> Script d'appel
            </Button>
          </div>
        </div>

        <ProspectionsDashboard onOpenProspect={openProspect} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(CATEGORY_META).map(([cat, meta]) => {
            const Icon = meta.icon;
            const s = stats[cat] || { total: 0, by_statut: {} };
            const signed = s.by_statut?.['Convention signée'] || 0;
            const interested = (s.by_statut?.['Intéressé'] || 0) + (s.by_statut?.['Convention envoyée'] || 0);
            return (
              <button
                key={cat}
                onClick={() => goCategory(cat)}
                className="group text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-400 hover:shadow-lg transition-all overflow-hidden"
                data-testid={`prospections-cat-${cat}`}
              >
                <div className={`h-24 bg-gradient-to-br ${meta.color} relative`}>
                  <Icon className="w-14 h-14 text-white/90 absolute top-4 right-4" />
                  <span className="absolute bottom-2 left-4 text-white text-xs uppercase tracking-wider font-semibold opacity-90">{meta.label}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-slate-900">{s.total}</span>
                    <span className="text-xs text-slate-500">prospect{s.total > 1 ? 's' : ''}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-emerald-700">• {interested} intéressé{interested > 1 ? 's' : ''}</span>
                    <span className="text-teal-700">• {signed} signé{signed > 1 ? 's' : ''}</span>
                  </div>
                  <div className="mt-3 flex items-center text-slate-400 group-hover:text-slate-700 text-xs">
                    Ouvrir <ChevronRight className="w-3 h-3 ml-1" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <ProspectionGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} defaultTab={guideDefaultTab} />
      </div>
    );
  }

  // ---------- CATEGORY (liste) ----------
  if (view === 'category') {
    const meta = CATEGORY_META[activeCategory];
    const Icon = meta.icon;
    return (
      <div className="space-y-4" data-testid="prospections-list">
        <button onClick={() => { setView('home'); loadStats(); }} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          ← Toutes les catégories
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{meta.label}</h1>
              <p className="text-xs text-slate-500">{items.length} prospect{items.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeCategory === 'mairie' && (
              <Button onClick={() => setShowImport(true)} variant="outline" data-testid="prospections-import-btn">
                <Upload className="w-4 h-4 mr-1.5" /> Importer Excel
              </Button>
            )}
            <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prospections-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau prospect
            </Button>
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, ville, SIRET…)" className="pl-9" data-testid="prospections-search" />
        </div>

        {/* Chips filtre + tri (mairies uniquement) */}
        {activeCategory === 'mairie' && (
          <div className="flex flex-wrap items-center gap-2 -mt-1" data-testid="prospections-mairie-filters">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Trier :</span>
            {[
              { id: 'ordre', label: '📞 Ordre d\'appel' },
              { id: 'fiabilite', label: '✅ Fiabilité haute d\'abord' },
              { id: 'population', label: '👥 Population décroissante' },
              { id: 'nom', label: '🔤 Alphabétique' },
            ].map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => setSortMode(o.id)}
                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition ${
                  sortMode === o.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
                data-testid={`sort-${o.id}`}
              >
                {o.label}
              </button>
            ))}
            <span className="mx-2 h-4 w-px bg-slate-200" />
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Fiabilité :</span>
            {[
              { id: 'all', label: 'Toutes', color: 'text-slate-600 border-slate-200' },
              { id: 'haute', label: 'Haute', color: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
              { id: 'moyenne', label: 'Moyenne', color: 'text-amber-700 border-amber-300 bg-amber-50' },
              { id: 'à vérifier', label: 'À vérifier', color: 'text-red-700 border-red-300 bg-red-50' },
            ].map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFiabiliteFilter(f.id)}
                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition ${
                  fiabiliteFilter === f.id
                    ? 'bg-slate-900 text-white border-slate-900'
                    : `bg-white hover:border-slate-400 ${f.color}`
                }`}
                data-testid={`fiab-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabs bucket */}
        <div className="flex gap-1 border-b border-slate-200">
          {[
            { id: 'chaud', label: 'En attente', color: 'text-amber-700 border-amber-500' },
            { id: 'actif', label: 'Partenaires actifs', color: 'text-emerald-700 border-emerald-500' },
            { id: 'mort', label: 'Marchés publics', color: 'text-slate-500 border-slate-400' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setBucket(tab.id)}
              className={`px-3 md:px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                bucket === tab.id
                  ? tab.color
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`prospections-tab-${tab.id}`}
            >
              {tab.label} <span className="ml-1 text-xs opacity-60">({bucketed[tab.id]?.length || 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>
        ) : (bucketed[bucket] || []).length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">
              {search ? 'Aucun prospect ne correspond' :
               bucket === 'chaud' ? 'Aucun prospect en cours de démarchage' :
               bucket === 'actif' ? 'Aucun partenaire actif' :
               'Aucune mairie en marchés publics uniquement'}
            </p>
            {!search && bucket === 'chaud' && (
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Ajouter le premier
              </Button>
            )}
          </div>
        ) : (
          <ProspectsTable
            items={bucketed[bucket] || []}
            meta={meta}
            Icon={Icon}
            onOpen={(pid) => { setActiveProspectId(pid); setView('detail'); }}
            onContact={(p) => setShowContactModal(p)}
          />
        )}

        {showContactModal && (
          <ContactModal
            prospect={showContactModal}
            onClose={() => setShowContactModal(null)}
            onSaved={() => { setShowContactModal(null); loadCategory(activeCategory); }}
          />
        )}

        {showCreate && (
          <CreateProspectModal
            category={activeCategory}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); loadCategory(activeCategory); loadStats(); }}
          />
        )}

        {showImport && activeCategory === 'mairie' && (
          <ImportXlsxModal
            category={activeCategory}
            onClose={() => setShowImport(false)}
            onImported={() => { setShowImport(false); loadCategory(activeCategory); loadStats(); }}
          />
        )}

        <FloatingScriptButton onClick={() => { setGuideDefaultTab('script'); setGuideOpen(true); }} />
        <ProspectionGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} defaultTab={guideDefaultTab} />
      </div>
    );
  }

  // ---------- DETAIL ----------
  const currentIdx = filtered.findIndex(p => p.prospect_id === activeProspectId);
  const prevProspect = currentIdx > 0 ? filtered[currentIdx - 1] : null;
  const nextProspect = currentIdx >= 0 && currentIdx < filtered.length - 1 ? filtered[currentIdx + 1] : null;
  return (
    <>
      <ProspectDetail
        prospectId={activeProspectId}
        prevProspect={prevProspect}
        nextProspect={nextProspect}
        currentIdx={currentIdx}
        totalCount={filtered.length}
        onGoPrev={() => prevProspect && setActiveProspectId(prevProspect.prospect_id)}
        onGoNext={() => nextProspect && setActiveProspectId(nextProspect.prospect_id)}
        onBack={() => { setView('category'); loadCategory(activeCategory); }}
        onDeleted={() => { setView('category'); loadCategory(activeCategory); loadStats(); }}
      />
      <FloatingScriptButton onClick={() => { setGuideDefaultTab('script'); setGuideOpen(true); }} />
      <ProspectionGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} defaultTab={guideDefaultTab} />
    </>
  );
}

// ============================================================================
// Create Prospect Modal
// ============================================================================
function CreateProspectModal({ category, onClose, onCreated }) {
  const [form, setForm] = useState({ nom_societe: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.nom_societe.trim()) { toast.error('Nom de la société requis'); return; }
    setSaving(true);
    try {
      await api.post('/prospects', { ...form, category });
      toast.success('Prospect ajouté');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la création');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="prospect-create-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-slate-900">Nouveau prospect — {CATEGORY_META[category].label}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}><X className="w-5 h-5" /></Button>
        </div>
        <div className="space-y-3">
          <Field label="Nom / Raison sociale *">
            <Input value={form.nom_societe} onChange={(e) => setForm({ ...form, nom_societe: e.target.value })} placeholder="ex : Étude Maître Dupont" data-testid="prospect-new-nom" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SIRET"><Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="14 chiffres" inputMode="numeric" /></Field>
            <Field label="Téléphone"><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></Field>
          </div>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Adresse"><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code postal"><Input value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} /></Field>
            <Field label="Ville"><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></Field>
          </div>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full min-h-[80px] border border-slate-200 rounded-md p-2 text-sm"
              placeholder="Contexte, contact, historique…"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prospect-new-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Créer
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

// ============================================================================
// Import XLSX Modal (mairies)
// ============================================================================
function ImportXlsxModal({ category, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const analyze = async (f) => {
    setAnalyzing(true); setPreview(null);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/prospects/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyse du fichier échouée');
    }
    setAnalyzing(false);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Seuls les fichiers .xlsx sont acceptés');
      return;
    }
    setFile(f);
    analyze(f);
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('skip_duplicates', String(skipDuplicates));
    try {
      const r = await api.post('/prospects/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { inserted, skipped } = r.data;
      toast.success(`✓ ${inserted} prospect(s) importé(s)${skipped ? ` — ${skipped} doublon(s) ignoré(s)` : ''}`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import échoué');
    }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="prospect-import-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-slate-700" /> Importer un fichier Excel — {CATEGORY_META[category]?.label}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Format .xlsx uniquement. Le fichier doit contenir au minimum la colonne <code>commune</code>.
              Colonnes reconnues : commune, type_entite, population, niveau, contact, fonction, numero_a_composer, email, fraicheur, fiabilite, script_appel, url_source, ordre.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={importing}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Dropzone */}
        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition"
               data-testid="prospect-import-dropzone">
          <input type="file" accept=".xlsx" onChange={onFileChange} className="hidden" disabled={importing || analyzing} />
          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">
            {file ? file.name : 'Glisse ton fichier .xlsx ici, ou clique pour parcourir'}
          </p>
          {file && (
            <p className="text-xs text-slate-500 mt-1">
              {(file.size / 1024).toFixed(1)} Ko — clique à nouveau pour changer
            </p>
          )}
        </label>

        {analyzing && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Analyse du fichier…
          </div>
        )}

        {preview && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total lignes</div>
                <div className="text-2xl font-bold text-slate-900">{preview.total}</div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Nouvelles</div>
                <div className="text-2xl font-bold text-emerald-700">{preview.new}</div>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Doublons</div>
                <div className="text-2xl font-bold text-amber-700">{preview.duplicates}</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Aperçu (5 premières lignes)
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold">#</th>
                      <th className="text-left px-2 py-2 font-semibold">Commune</th>
                      <th className="text-left px-2 py-2 font-semibold">Type</th>
                      <th className="text-right px-2 py-2 font-semibold">Pop.</th>
                      <th className="text-left px-2 py-2 font-semibold">Téléphone</th>
                      <th className="text-left px-2 py-2 font-semibold">Fiabilité</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(preview.sample || []).map((r, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5 text-slate-500">{r.ordre_import || i + 1}</td>
                        <td className="px-2 py-1.5 font-medium text-slate-900">{r.nom_societe}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{r.type_entite || '—'}</Badge></td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.population?.toLocaleString('fr-FR') || '—'}</td>
                        <td className="px-2 py-1.5 tabular-nums">{r.telephone || '—'}</td>
                        <td className="px-2 py-1.5">{r.fiabilite || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)}
                     className="rounded border-slate-300" />
              Ignorer les prospects déjà existants (même nom)
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={importing}>Annuler</Button>
          <Button onClick={doImport} disabled={!preview || importing || analyzing}
                  className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prospect-import-confirm">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {preview ? `Importer ${skipDuplicates ? preview.new : preview.total} prospect${(skipDuplicates ? preview.new : preview.total) > 1 ? 's' : ''}` : 'Importer'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Detail page
// ============================================================================
function ProspectDetail({ prospectId, onBack, onDeleted,
                          prevProspect, nextProspect, currentIdx, totalCount,
                          onGoPrev, onGoNext }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [generatingPlaquette, setGeneratingPlaquette] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sendingConvention, setSendingConvention] = useState(false);
  const logoInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/prospects/${prospectId}`);
      setP(r.data);
      setEditForm(r.data);
    } catch (e) {
      toast.error('Prospect introuvable');
      onBack();
    }
    setLoading(false);
  };

  useEffect(() => { if (prospectId) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [prospectId]);

  // Raccourcis ← / → (ignorés si l'utilisateur tape dans un champ)
  useEffect(() => {
    const handler = (e) => {
      const t = e.target?.tagName;
      if (t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || e.target?.isContentEditable) return;
      if (e.key === 'ArrowLeft' && prevProspect && onGoPrev) { e.preventDefault(); onGoPrev(); }
      if (e.key === 'ArrowRight' && nextProspect && onGoNext) { e.preventDefault(); onGoNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prevProspect, nextProspect, onGoPrev, onGoNext]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/prospects/${prospectId}`, editForm);
      setP(r.data);
      setEditing(false);
      toast.success('Prospect mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la mise à jour');
    }
    setSaving(false);
  };

  const updateStatut = async (newStatut) => {
    try {
      const r = await api.put(`/prospects/${prospectId}`, { statut: newStatut });
      setP(r.data); setEditForm(r.data);
      toast.success(`Statut → ${newStatut}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du changement de statut');
    }
  };

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      await api.post(`/prospects/${prospectId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo importé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'import du logo');
    }
    setUploadingLogo(false);
    try { e.target.value = ''; } catch (_) { /* noop */ }
  };

  const downloadPlaquette = async () => {
    setGeneratingPlaquette(true);
    try {
      const r = await api.get(`/prospects/${prospectId}/plaquette`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Plaquette_${(p?.nom_societe || 'prospect').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plaquette téléchargée');
    } catch (e) {
      // try to extract JSON error from blob
      try {
        const txt = await e?.response?.data?.text?.();
        const j = txt ? JSON.parse(txt) : null;
        toast.error(j?.detail || 'Échec de génération', { duration: 10000 });
      } catch (_) {
        toast.error(e?.response?.data?.detail || 'Échec de génération', { duration: 10000 });
      }
    }
    setGeneratingPlaquette(false);
  };

  const sendConvention = async () => {
    if (!p?.email) { toast.error('Renseignez d\'abord l\'email du prospect'); return; }
    if (!window.confirm(`Envoyer la convention apporteur d'affaires par email à ${p.email} ?`)) return;
    setSendingConvention(true);
    try {
      const r = await api.post(`/prospects/${prospectId}/convention/send`);
      toast.success(`Email envoyé à ${r.data.sent_to}`, { duration: 6000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'envoi', { duration: 12000 });
    }
    setSendingConvention(false);
  };

  const downloadConvention = async () => {
    try {
      const r = await api.get(`/prospects/${prospectId}/convention/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Convention_${p.convention_has_signed_pdf ? 'signee_' : ''}${(p?.nom_societe || 'prospect').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Aucune convention disponible');
    }
  };

  const deleteProspect = async () => {
    if (!window.confirm(`Supprimer définitivement ${p.nom_societe} ?`)) return;
    try {
      await api.delete(`/prospects/${prospectId}`);
      toast.success('Prospect supprimé');
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  if (loading || !p) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const meta = CATEGORY_META[p.category] || CATEGORY_META.agence;
  const Icon = meta.icon;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  return (
    <div className="space-y-4" data-testid="prospect-detail">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="prospect-back">
          ← Retour à la liste
        </button>
        {(prevProspect || nextProspect) && (
          <div className="flex items-center gap-2" data-testid="prospect-prev-next-nav">
            <button
              type="button"
              onClick={onGoPrev}
              disabled={!prevProspect}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
              title={prevProspect ? `← ${prevProspect.nom_societe}` : 'Aucun précédent'}
              data-testid="prospect-prev-btn"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
              <span className="max-w-[160px] truncate">
                {prevProspect ? prevProspect.nom_societe : 'Précédent'}
              </span>
            </button>
            {typeof currentIdx === 'number' && totalCount > 0 && (
              <span className="text-[11px] text-slate-500 font-medium tabular-nums px-1">
                {currentIdx + 1} / {totalCount}
              </span>
            )}
            <button
              type="button"
              onClick={onGoNext}
              disabled={!nextProspect}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
              title={nextProspect ? `→ ${nextProspect.nom_societe}` : 'Aucun suivant'}
              data-testid="prospect-next-btn"
            >
              <span className="max-w-[160px] truncate">
                {nextProspect ? nextProspect.nom_societe : 'Suivant'}
              </span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="shrink-0">
            {p.has_logo ? (
              <img
                src={`${apiBase}/prospects/${prospectId}/logo`}
                alt="Logo"
                className="w-20 h-20 rounded-xl border border-slate-200 object-contain bg-white"
              />
            ) : (
              <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center`}>
                <Icon className="w-10 h-10 text-white" />
              </div>
            )}
            <input type="file" accept="image/*" ref={logoInputRef} className="hidden" onChange={onLogoChange} data-testid="prospect-logo-input" />
            <button
              onClick={() => logoInputRef.current?.click()}
              disabled={uploadingLogo}
              className="text-[11px] text-slate-500 hover:text-slate-900 mt-2 inline-flex items-center gap-1"
            >
              {uploadingLogo ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
              {p.has_logo ? 'Changer le logo' : 'Ajouter un logo'}
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">{p.nom_societe}</h1>
              <Badge className={STATUT_STYLE[p.statut] || 'bg-slate-100'}>{p.statut}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-1">{meta.label}{p.siret ? ` · SIRET ${p.siret}` : ''}</p>

            {/* Statut switcher */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Étape :</label>
              <select
                value={p.statut}
                onChange={(e) => updateStatut(e.target.value)}
                className="text-sm border border-slate-200 rounded-md px-2 py-1 bg-white"
                data-testid="prospect-statut-select"
              >
                {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            <Button onClick={() => setEditing(true)} variant="outline" size="sm" data-testid="prospect-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
            </Button>
            <Button onClick={deleteProspect} variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="prospect-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
            </Button>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-sm">
          <InfoBlock label="Email">{p.email || '—'}</InfoBlock>
          <InfoBlock label="Téléphone">{p.telephone || '—'}</InfoBlock>
          <InfoBlock label="Adresse">
            {[p.adresse, [p.code_postal, p.ville].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || '—'}
          </InfoBlock>
        </div>
        {p.notes && (
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
            {p.notes}
          </div>
        )}
      </div>

      {/* Actions cards */}
      <div className="grid grid-cols-1 gap-4">
        <ActionCard
          title="Plaquette personnalisée"
          description="Génère un PDF avec page de couverture (logo + nom + ville) + votre modèle de plaquette."
          icon={<FileText className="w-6 h-6 text-blue-600" />}
          color="border-blue-200 bg-blue-50/40"
        >
          <Button onClick={downloadPlaquette} disabled={generatingPlaquette} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="prospect-plaquette-btn">
            {generatingPlaquette ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Générer la plaquette
          </Button>
        </ActionCard>
      </div>

      {/* Dossier commercial structuré + Journal des échanges */}
      <ProspectDossierPanel
        prospectId={prospectId}
        category={p.category}
        nomSociete={p.nom_societe}
      />

      {editing && (
        <EditProspectModal
          form={editForm}
          setForm={setEditForm}
          saving={saving}
          onCancel={() => { setEditing(false); setEditForm(p); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">{label}</p>
      <p className="text-slate-900">{children}</p>
    </div>
  );
}

function ActionCard({ title, description, icon, color, children }) {
  return (
    <div className={`rounded-2xl border p-5 ${color}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0">{icon}</div>
        <div>
          <h3 className="font-bold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-600 mt-1">{description}</p>
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function EditProspectModal({ form, setForm, saving, onCancel, onSave }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">Modifier le prospect</h2>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}><X className="w-5 h-5" /></Button>
        </div>
        <div className="space-y-3">
          <Field label="Nom / Raison sociale *">
            <Input value={form.nom_societe || ''} onChange={(e) => setForm({ ...form, nom_societe: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SIRET"><Input value={form.siret || ''} onChange={(e) => setForm({ ...form, siret: e.target.value })} inputMode="numeric" /></Field>
            <Field label="Téléphone"><Input value={form.telephone || ''} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></Field>
          </div>
          <Field label="Email"><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Adresse"><Input value={form.adresse || ''} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code postal"><Input value={form.code_postal || ''} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} /></Field>
            <Field label="Ville"><Input value={form.ville || ''} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></Field>
          </div>
          <Field label="Notes">
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full min-h-[80px] border border-slate-200 rounded-md p-2 text-sm"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onCancel} disabled={saving}>Annuler</Button>
          <Button onClick={onSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ProspectsTable — vue tableau avec dernier contact + prochaine relance
// ============================================================================
const CONTACT_TYPE_META = {
  mail: { label: 'Mail', icon: Mail, cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  telephone: { label: 'Tél', icon: Phone, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  visite: { label: 'Visite', icon: MapPin, cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  sms: { label: 'SMS', icon: MessageSquare, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function fmtFR(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (e) { return iso; }
}

function ProspectsTable({ items, meta, Icon, onOpen, onContact }) {
  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {items.map((p) => {
          const last = (p.contact_history || [])[0];
          const isDue = p.is_relance_due;
          return (
            <div key={p.prospect_id} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`prospect-card-${p.prospect_id}`}>
              <button
                onClick={() => onOpen(p.prospect_id)}
                className="w-full text-left"
              >
                <div className="flex items-start gap-2 mb-1.5">
                  <div className={`w-8 h-8 rounded-lg ${meta.accent} flex items-center justify-center shrink-0`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 truncate">{p.nom_societe}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {[p.code_postal, p.ville].filter(Boolean).join(' ')}
                    </p>
                  </div>
                  <Badge className={`${STATUT_STYLE[p.statut] || 'bg-slate-100'} text-[10px]`}>{p.statut}</Badge>
                </div>
              </button>
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-1 flex-wrap">
                  {last ? (
                    <>
                      {(last.types || []).map((t) => {
                        const m = CONTACT_TYPE_META[t];
                        if (!m) return null;
                        const TI = m.icon;
                        return <span key={t} className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border text-[9px] ${m.cls}`}><TI className="w-2.5 h-2.5" />{m.label}</span>;
                      })}
                      <span className="text-[10px] text-slate-400">{fmtFR(last.date)}</span>
                    </>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">Aucun contact</span>
                  )}
                </div>
                {isDue && <AlertCircle className="w-3.5 h-3.5 text-red-500" title="Relance due" />}
                <Button size="sm" variant="outline" onClick={() => onContact(p)} className="h-7 px-2 text-xs">
                  <Plus className="w-3 h-3 mr-0.5" /> Contact
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="w-full" role="table">
          <div role="row" className="grid grid-cols-[2fr_1.2fr_1fr_1.4fr_1fr_1fr] bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase">
            <div className="px-4 py-2.5">Prospect</div>
            <div className="px-2 py-2.5">Ville</div>
            <div className="px-2 py-2.5">Statut</div>
            <div className="px-2 py-2.5">Dernier contact</div>
            <div className="px-2 py-2.5">Prochaine relance</div>
            <div className="px-2 py-2.5 text-right">Actions</div>
          </div>
          <div role="rowgroup" className="divide-y divide-slate-100">
            {items.map((p) => {
              const last = (p.contact_history || [])[0];
              const isDue = p.is_relance_due;
              return (
                <div
                  key={p.prospect_id}
                  role="row"
                  className="grid grid-cols-[2fr_1.2fr_1fr_1.4fr_1fr_1fr] items-center hover:bg-slate-50 transition-colors"
                  data-testid={`prospect-row-${p.prospect_id}`}
                >
                  <button onClick={() => onOpen(p.prospect_id)} className="px-4 py-3 text-left min-w-0" data-testid={`prospect-open-${p.prospect_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-8 h-8 rounded-lg ${meta.accent} flex items-center justify-center shrink-0`}>
                        {p.ordre_import ? (
                          <span className="text-[10px] font-bold tabular-nums">#{p.ordre_import}</span>
                        ) : (
                          <Icon className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-slate-900 truncate">{p.nom_societe}</p>
                        {(p.population || p.fiabilite || p.type_entite) && (
                          <div className="flex items-center gap-1 mt-0.5">
                            {p.type_entite && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                {p.type_entite}
                              </span>
                            )}
                            {p.population && (
                              <span className="text-[10px] text-slate-500 tabular-nums">
                                {p.population.toLocaleString('fr-FR')} hab
                              </span>
                            )}
                            {p.fiabilite && (
                              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${
                                p.fiabilite === 'haute' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                p.fiabilite === 'moyenne' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-red-50 text-red-700 border-red-200'
                              }`}>
                                {p.fiabilite}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="px-2 py-3 text-sm text-slate-600 truncate">
                    {p.ville || '—'}
                  </div>
                  <div className="px-2 py-3">
                    <Badge className={`${STATUT_STYLE[p.statut] || 'bg-slate-100'} text-[10px]`}>{p.statut}</Badge>
                  </div>
                  <div className="px-2 py-3">
                    {last ? (
                      <div className="flex flex-wrap items-center gap-1">
                        {(last.types || []).map((t) => {
                          const m = CONTACT_TYPE_META[t];
                          if (!m) return null;
                          const TI = m.icon;
                          return <span key={t} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] ${m.cls}`}><TI className="w-2.5 h-2.5" />{m.label}</span>;
                        })}
                        <span className="text-[11px] text-slate-500 ml-1">{fmtFR(last.date)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Aucun contact</span>
                    )}
                  </div>
                  <div className="px-2 py-3">
                    {p.next_relance_date ? (
                      <span className={`text-xs font-medium ${isDue ? 'text-red-600' : 'text-slate-700'}`}>
                        {isDue && <AlertCircle className="w-3 h-3 inline mr-0.5" />}
                        {fmtFR(p.next_relance_date)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </div>
                  <div className="px-2 py-3 flex items-center gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => onContact(p)} className="h-7 px-2 text-xs" data-testid={`log-contact-${p.prospect_id}`}>
                      <Plus className="w-3 h-3 mr-0.5" /> Contact
                    </Button>
                    <button onClick={() => onOpen(p.prospect_id)} className="p-1.5 text-slate-400 hover:text-slate-700">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// ContactModal — logger un contact multi-type (mail/tél/visite/sms)
// ============================================================================
function ContactModal({ prospect, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const toggleType = (t) => {
    setTypes((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const save = async () => {
    if (types.length === 0) {
      toast.error('Sélectionnez au moins un type de contact');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/prospects/${prospect.prospect_id}/contact`, { types, notes, date });
      toast.success('Contact enregistré');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Enregistrer un contact</h3>
            <p className="text-xs text-slate-500 truncate">{prospect.nom_societe}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">Type(s) de contact *</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CONTACT_TYPE_META).map(([k, m]) => {
                const active = types.includes(k);
                const TI = m.icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleType(k)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                      active ? `${m.cls} border-current` : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'
                    }`}
                    data-testid={`contact-type-${k}`}
                  >
                    <TI className="w-4 h-4" />
                    {m.label}
                    {active && <CheckCircle2 className="w-3.5 h-3.5 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex : rdv fixé au 15/03, demande devis, etc."
              className="w-full min-h-[70px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="contact-notes"
            />
          </div>

          {types.includes('mail') && (
            <p className="text-[11px] text-blue-600 bg-blue-50 border border-blue-200 rounded p-2">
              💡 Une prochaine relance sera automatiquement programmée à +14 jours.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="save-contact" className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}

